"use strict";
/**
 * Permission Helper
 *
 * Centralised permission management for camera and location access.
 * Handles permission checking, requesting, and status monitoring.
 *
 * Browser Support:
 * - Chrome/Edge: Full Permissions API support
 * - Firefox: Partial support (geolocation works, camera experimental)
 * - Safari: No Permissions API (uses fallback)
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBrowserPermissionInstructions = exports.requestAllRequiredPermissions = exports.checkAllRequiredPermissions = exports.requestLocationPermission = exports.checkLocationPermission = exports.requestCameraPermission = exports.checkCameraPermission = void 0;
/**
 * Check if camera permission is granted
 * Uses Permissions API where available, falls back to feature detection
 */
function checkCameraPermission() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if Permissions API is supported
            if ('permissions' in navigator && 'query' in navigator.permissions) {
                try {
                    const result = yield navigator.permissions.query({ name: 'camera' });
                    return {
                        granted: result.state === 'granted',
                        status: result.state,
                    };
                }
                catch (error) {
                    console.warn('⚠️ Permissions API query failed for camera, using fallback', error);
                }
            }
            // Fallback: Check if getUserMedia is available
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                return {
                    granted: false,
                    status: 'prompt', // Assume we need to prompt
                };
            }
            // Browser doesn't support camera access
            return {
                granted: false,
                status: 'unsupported',
                error: 'Camera access is not supported in this browser',
            };
        }
        catch (error) {
            console.error('❌ Failed to check camera permission:', error);
            return {
                granted: false,
                status: 'unsupported',
                error: error.message || 'Unknown error checking camera permission',
            };
        }
    });
}
exports.checkCameraPermission = checkCameraPermission;
/**
 * Request camera permission from user
 * Triggers browser's camera permission prompt
 */
function requestCameraPermission() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                return {
                    granted: false,
                    status: 'unsupported',
                    error: 'Camera access is not supported in this browser',
                };
            }
            // Request camera access - this triggers the browser permission prompt
            // We immediately stop the stream after getting permission
            const stream = yield navigator.mediaDevices.getUserMedia({
                video: true,
                audio: false,
            });
            // Stop all tracks immediately - we just needed to trigger the permission
            stream.getTracks().forEach(track => track.stop());
            return {
                granted: true,
                status: 'granted',
            };
        }
        catch (error) {
            console.error('❌ Camera permission denied or unavailable:', error);
            // Provide user-friendly error messages
            let errorMessage = 'Failed to access camera';
            let status = 'denied';
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage = 'Camera permission was denied';
                status = 'denied';
            }
            else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                errorMessage = 'No camera device found';
                status = 'unsupported';
            }
            else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                errorMessage = 'Camera is already in use by another application';
                status = 'denied';
            }
            else if (error.name === 'OverconstrainedError') {
                errorMessage = 'Camera constraints could not be satisfied';
                status = 'unsupported';
            }
            else if (error.name === 'SecurityError') {
                errorMessage = 'Camera access is blocked by security policy';
                status = 'denied';
            }
            return {
                granted: false,
                status,
                error: errorMessage,
            };
        }
    });
}
exports.requestCameraPermission = requestCameraPermission;
/**
 * Check if location permission is granted
 * Uses Permissions API where available, falls back to feature detection
 */
function checkLocationPermission() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // Check if Permissions API is supported
            if ('permissions' in navigator && 'query' in navigator.permissions) {
                try {
                    const result = yield navigator.permissions.query({ name: 'geolocation' });
                    return {
                        granted: result.state === 'granted',
                        status: result.state,
                    };
                }
                catch (error) {
                    console.warn('⚠️ Permissions API query failed for geolocation, using fallback', error);
                }
            }
            // Fallback: Check if geolocation is available
            if ('geolocation' in navigator) {
                return {
                    granted: false,
                    status: 'prompt', // Assume we need to prompt
                };
            }
            // Browser doesn't support geolocation
            return {
                granted: false,
                status: 'unsupported',
                error: 'Location access is not supported in this browser',
            };
        }
        catch (error) {
            console.error('❌ Failed to check location permission:', error);
            return {
                granted: false,
                status: 'unsupported',
                error: error.message || 'Unknown error checking location permission',
            };
        }
    });
}
exports.checkLocationPermission = checkLocationPermission;
/**
 * Request location permission from user
 * Triggers browser's location permission prompt
 *
 * Note: This is a simpler version of locationHelper.requestLocationPermission()
 * focused only on permission granting, not location retrieval
 */
function requestLocationPermission() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (!('geolocation' in navigator)) {
                return {
                    granted: false,
                    status: 'unsupported',
                    error: 'Location access is not supported in this browser',
                };
            }
            // Request location access - this triggers the browser permission prompt
            yield new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 10000,
                    maximumAge: 0,
                    enableHighAccuracy: false, // Low accuracy is fine for permission check
                });
            });
            return {
                granted: true,
                status: 'granted',
            };
        }
        catch (error) {
            console.error('❌ Location permission denied or unavailable:', error);
            // Provide user-friendly error messages based on error code
            let errorMessage = 'Failed to access location';
            let status = 'denied';
            if (error.code === 1) {
                // PERMISSION_DENIED
                errorMessage = 'Location permission was denied';
                status = 'denied';
            }
            else if (error.code === 2) {
                // POSITION_UNAVAILABLE
                errorMessage = 'Location is currently unavailable';
                status = 'unsupported';
            }
            else if (error.code === 3) {
                // TIMEOUT
                errorMessage = 'Location request timed out';
                status = 'denied';
            }
            return {
                granted: false,
                status,
                error: errorMessage,
            };
        }
    });
}
exports.requestLocationPermission = requestLocationPermission;
/**
 * Check if all required permissions (camera + location) are granted
 */
function checkAllRequiredPermissions() {
    return __awaiter(this, void 0, void 0, function* () {
        const [camera, location] = yield Promise.all([
            checkCameraPermission(),
            checkLocationPermission(),
        ]);
        // Location is mandatory, camera is optional (FaceTec will fall back to QR if unavailable)
        const allGranted = location.granted;
        return {
            allGranted,
            camera,
            location,
        };
    });
}
exports.checkAllRequiredPermissions = checkAllRequiredPermissions;
/**
 * Request all required permissions (camera + location)
 * Requests them sequentially to avoid overwhelming the user with multiple prompts at once
 */
function requestAllRequiredPermissions() {
    return __awaiter(this, void 0, void 0, function* () {
        // Camera is optional; request best-effort for FaceTec, but don't block on failure.
        const camera = yield requestCameraPermission();
        // Location is mandatory.
        const location = yield requestLocationPermission();
        if (!location.granted) {
            return {
                allGranted: false,
                camera,
                location,
                failureReason: location.error || 'Location permission denied',
            };
        }
        return {
            allGranted: true,
            camera,
            location,
        };
    });
}
exports.requestAllRequiredPermissions = requestAllRequiredPermissions;
/**
 * Get browser-specific instructions for enabling permissions manually
 */
function getBrowserPermissionInstructions() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('chrome')) {
        return [
            '1. Click the lock icon (🔒) or settings icon in the address bar',
            '2. Find "Camera" and "Location" in the permissions list',
            '3. Change both to "Allow"',
            '4. Reload this page',
        ];
    }
    else if (userAgent.includes('firefox')) {
        return [
            '1. Click the lock icon (🔒) in the address bar',
            '2. Click "Clear these permissions"',
            '3. Reload this page and grant permissions when prompted',
        ];
    }
    else if (userAgent.includes('safari')) {
        return [
            '1. Go to Safari > Preferences > Websites',
            '2. Select "Camera" and "Location" in the sidebar',
            '3. Find this website and change to "Allow"',
            '4. Reload this page',
        ];
    }
    else if (userAgent.includes('edge')) {
        return [
            '1. Click the lock icon (🔒) or settings icon in the address bar',
            '2. Find "Camera" and "Location" in the permissions list',
            '3. Change both to "Allow"',
            '4. Reload this page',
        ];
    }
    else {
        return [
            '1. Look for a permissions icon in your browser\'s address bar',
            '2. Enable camera and location permissions for this site',
            '3. Reload this page',
        ];
    }
}
exports.getBrowserPermissionInstructions = getBrowserPermissionInstructions;
